import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Youtube, Video, Users, Eye, ThumbsUp, MessageCircle, PlusCircle, Trash2,
  Loader2, PlayCircle, ListMusic, Radio, Link2, Send, Bell, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

const CATEGORIES = [
  { id: "22", label: "People & Blogs" },
  { id: "24", label: "Entertainment" },
  { id: "26", label: "Howto & Style" },
  { id: "27", label: "Education" },
  { id: "10", label: "Music" },
  { id: "17", label: "Sports" },
];

export default function YouTubePage() {
  const { user } = useAuth();
  const canManage = user?.role === "master" || user?.role === "admin";

  const [conn, setConn] = useState(null);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState({ local: [], remote: [], connected: false });
  const [playlists, setPlaylists] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [connectOpen, setConnectOpen] = useState(false);
  const [connectForm, setConnectForm] = useState({ channel_id: "", channel_title: "", composio_account_id: "", composio_user_id: "" });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "", description: "", tags: "", category_id: "22", privacy: "private",
    video_url: "", thumbnail_url: "", scheduled_at: "",
  });
  const [uploading, setUploading] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);

  const [plOpen, setPlOpen] = useState(false);
  const [plForm, setPlForm] = useState({ title: "", description: "", privacy: "private" });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({ video_id: "", metric: "views", threshold: 1000 });

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, v, p, cm] = await Promise.all([
        api.get("/youtube/connection"),
        api.get("/youtube/stats"),
        api.get("/youtube/videos"),
        api.get("/youtube/playlists"),
        api.get("/youtube/comments"),
      ]);
      setConn(c.data);
      setStats(s.data);
      setVideos(v.data);
      setPlaylists(p.data.items || []);
      setComments(cm.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar YouTube");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doConnect = async () => {
    if (!connectForm.channel_id.trim() || !connectForm.composio_account_id.trim() || !connectForm.composio_user_id.trim()) {
      toast.error("Todos los campos son obligatorios"); return;
    }
    try {
      await api.post("/youtube/connection", connectForm);
      toast.success("Canal conectado");
      setConnectOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doDisconnect = async () => {
    if (!window.confirm("¿Desconectar el canal de YouTube?")) return;
    try {
      await api.delete("/youtube/connection");
      toast.success("Canal desconectado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const uploadThumbnail = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/photo?module=youtube_thumb", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadForm((f) => ({ ...f, thumbnail_url: r.data.url }));
      toast.success("Miniatura subida");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error subiendo miniatura");
    } finally { setThumbUploading(false); }
  };

  const doUpload = async () => {
    if (!uploadForm.title.trim()) { toast.error("Título obligatorio"); return; }
    if (!uploadForm.video_url.trim()) { toast.error("URL pública de video obligatoria"); return; }
    if (uploadForm.video_url.startsWith("data:")) { toast.error("El video debe ser una URL pública, no base64"); return; }
    setUploading(true);
    try {
      const payload = {
        ...uploadForm,
        tags: uploadForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        scheduled_at: uploadForm.scheduled_at ? new Date(uploadForm.scheduled_at).toISOString() : null,
      };
      await api.post("/youtube/videos", payload);
      toast.success(uploadForm.scheduled_at ? "Video programado" : "Video enviado");
      setUploadOpen(false);
      setUploadForm({ title: "", description: "", tags: "", category_id: "22", privacy: "private", video_url: "", thumbnail_url: "", scheduled_at: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al subir");
    } finally { setUploading(false); }
  };

  const doCreatePlaylist = async () => {
    if (!plForm.title.trim()) { toast.error("Título obligatorio"); return; }
    try {
      await api.post("/youtube/playlists", plForm);
      toast.success("Playlist creada");
      setPlOpen(false);
      setPlForm({ title: "", description: "", privacy: "private" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doCreateAlert = async () => {
    if (!alertForm.video_id.trim() || !alertForm.threshold) { toast.error("Video y umbral obligatorios"); return; }
    try {
      await api.post("/youtube/alerts", { ...alertForm, threshold: parseInt(alertForm.threshold, 10) });
      toast.success("Alerta creada");
      setAlertOpen(false);
      setAlertForm({ video_id: "", metric: "views", threshold: 1000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const doDeleteVideo = async (v) => {
    if (!window.confirm(`¿Eliminar "${v.title}"?`)) return;
    try {
      await api.delete(`/youtube/videos/${v.id}`);
      toast.success("Video eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const notConnected = !conn?.connected;

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in-up" data-testid="youtube-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span className="inline-flex h-10 w-10 rounded-lg items-center justify-center text-white" style={{ backgroundColor: "#FF0000" }}>
              <Youtube className="h-5 w-5" strokeWidth={1.8} />
            </span>
            YouTube
          </h1>
          <p className="text-sm text-stone-500 mt-2">Gestiona tu canal: sube y programa videos, playlists, comentarios y alertas.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && notConnected && (
            <Button onClick={() => setConnectOpen(true)} data-testid="btn-connect-yt" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
              <Link2 className="h-4 w-4 mr-2" /> Conectar canal
            </Button>
          )}
          {canManage && !notConnected && (
            <Button variant="outline" onClick={doDisconnect} data-testid="btn-disconnect-yt" className="text-destructive">
              Desconectar canal
            </Button>
          )}
          <Button onClick={() => setUploadOpen(true)} data-testid="btn-upload-video" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
            <PlusCircle className="h-4 w-4 mr-2" /> Subir video
          </Button>
        </div>
      </div>

      {notConnected && (
        <div className="card-elevated p-5 border-brand-gold/40 bg-brand-gold-soft/30" data-testid="yt-not-connected-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-brand-gold-dark shrink-0 mt-0.5" />
            <div className="text-sm text-brand-ink">
              <strong>Canal no conectado.</strong> Los videos se guardarán como <em>simulados</em> hasta que un Master/Admin conecte una cuenta YouTube vía Composio (Centro de Conectividad).
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas del canal */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="yt-stats">
        {[
          { key: "subscribers", label: "Suscriptores", value: stats?.subscribers, Icon: Users },
          { key: "views", label: "Vistas totales", value: stats?.views, Icon: Eye },
          { key: "videos", label: "Videos", value: stats?.videos, Icon: Video },
          { key: "watch_time", label: "Tiempo de reproducción", value: null, Icon: PlayCircle, disabled: true },
        ].map(({ key, label, value, Icon, disabled }) => (
          <div key={key} className={`card-elevated p-4 ${disabled ? "opacity-50" : ""}`}>
            <div className="flex items-center gap-2 text-stone-500 mb-1">
              <Icon className="h-4 w-4" />
              <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
            </div>
            <div className="font-serif-display text-2xl sm:text-3xl text-brand-ink font-semibold">
              {disabled ? <span className="text-xs italic text-stone-400">No disponible en esta API</span>
                : value == null ? "—" : Number(value).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      {stats?.not_available?.length > 0 && (
        <p className="text-[11px] text-stone-500 -mt-2">
          <em>{stats.note || "Watch time e impressions requieren YouTube Analytics API"}</em>
        </p>
      )}

      {/* Biblioteca de videos */}
      <div>
        <h2 className="font-serif-display text-xl sm:text-2xl text-brand-ink mb-3 flex items-center gap-2">
          <Video className="h-5 w-5 text-brand-emerald" /> Biblioteca de videos
        </h2>
        {loading ? (
          <div className="card-elevated p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
        ) : (videos.local.length === 0 && videos.remote.length === 0) ? (
          <div className="card-elevated p-8 text-center text-stone-500" data-testid="yt-empty">Aún no hay videos. Sube el primero con el botón "Subir video".</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {videos.local.map((v) => (
              <div key={v.id} className="card-elevated p-4 space-y-2" data-testid={`yt-video-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-brand-ink text-sm break-words min-w-0 flex-1">{v.title}</h3>
                  <Badge className={
                    v.status === "published" ? "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30 border" :
                    v.status === "scheduled" ? "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40 border" :
                    v.status === "failed" ? "bg-destructive/10 text-destructive border-destructive/30 border" :
                    "bg-stone-100 text-stone-700 border-stone-200 border"
                  }>{v.status}</Badge>
                </div>
                {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full aspect-video object-cover rounded" />}
                <div className="text-xs text-stone-500 space-y-0.5">
                  <div>Creado: {fmt(v.created_at)}</div>
                  {v.scheduled_at && <div>Programado: {fmt(v.scheduled_at)}</div>}
                  {v.published_at && <div>Publicado: {fmt(v.published_at)}</div>}
                  {v.error && <div className="text-destructive">Error: {v.error}</div>}
                </div>
                <div className="flex gap-1 pt-1">
                  {v.video_id && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try {
                        const r = await api.get(`/youtube/videos/${v.id}/metrics`);
                        toast.message(`👁 ${r.data.views ?? 0} · 👍 ${r.data.likes ?? 0} · 💬 ${r.data.comments ?? 0}`);
                      } catch (e) { toast.error("Error"); }
                    }} data-testid={`yt-metrics-${v.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> Métricas
                    </Button>
                  )}
                  {v.video_id && (
                    <Button size="sm" variant="ghost" onClick={() => { setAlertForm((f) => ({ ...f, video_id: v.video_id })); setAlertOpen(true); }} data-testid={`yt-alert-${v.id}`}>
                      <Bell className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => doDeleteVideo(v)} className="text-destructive hover:bg-destructive/10 ml-auto" data-testid={`yt-delete-${v.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Playlists */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif-display text-xl sm:text-2xl text-brand-ink flex items-center gap-2">
            <ListMusic className="h-5 w-5 text-brand-emerald" /> Playlists
          </h2>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setPlOpen(true)} data-testid="btn-new-playlist" className="border-brand-emerald text-brand-emerald-dark">
              <PlusCircle className="h-4 w-4 mr-1" /> Nueva
            </Button>
          )}
        </div>
        {playlists.length === 0 ? (
          <div className="card-elevated p-6 text-center text-stone-500 text-sm">Sin playlists {notConnected && "(canal no conectado)"}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {playlists.map((p, i) => (
              <div key={p.id?.playlistId || p.id || i} className="card-elevated p-4">
                <div className="font-semibold text-brand-ink text-sm">{p.snippet?.title || p.title || "Sin título"}</div>
                <div className="text-xs text-stone-500 mt-1">{p.snippet?.description || p.description || ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comentarios */}
      <div>
        <h2 className="font-serif-display text-xl sm:text-2xl text-brand-ink mb-3 flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-brand-emerald" /> Comentarios recientes
        </h2>
        {comments.length === 0 ? (
          <div className="card-elevated p-6 text-center text-stone-500 text-sm" data-testid="yt-no-comments">Sin comentarios {notConnected && "(canal no conectado)"}</div>
        ) : (
          <div className="space-y-2">
            {comments.slice(0, 20).map((c, i) => {
              const top = c.snippet?.topLevelComment?.snippet || {};
              return (
                <div key={c.id || i} className="card-elevated p-3">
                  <div className="text-xs font-semibold text-brand-ink">{top.authorDisplayName || "Anónimo"}</div>
                  <div className="text-sm text-stone-700">{top.textDisplay || top.textOriginal}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Live streaming — no soportado */}
      <div className="card-elevated p-5 opacity-70" data-testid="yt-live-placeholder">
        <div className="flex items-start gap-3">
          <Radio className="h-5 w-5 text-brand-emerald shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-brand-ink">Transmisiones en vivo</h3>
            <p className="text-sm text-stone-500 mt-1">Próximamente — requiere YouTube Live Streaming API (Composio expone sólo lectura de chat en vivo).</p>
          </div>
        </div>
      </div>

      {/* --- Diálogos --- */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-lg" data-testid="dlg-connect-yt">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Conectar canal YouTube</DialogTitle>
            <DialogDescription>
              Requiere una cuenta conectada previamente en Composio (dashboard). Ingresa los IDs del canal + `connected_account_id` y `user_id` de Composio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Channel ID</Label><Input value={connectForm.channel_id} onChange={(e) => setConnectForm({ ...connectForm, channel_id: e.target.value })} data-testid="yt-channel-id" /></div>
            <div><Label>Nombre del canal (opcional)</Label><Input value={connectForm.channel_title} onChange={(e) => setConnectForm({ ...connectForm, channel_title: e.target.value })} data-testid="yt-channel-title" /></div>
            <div><Label>Composio Account ID</Label><Input value={connectForm.composio_account_id} onChange={(e) => setConnectForm({ ...connectForm, composio_account_id: e.target.value })} data-testid="yt-composio-account" /></div>
            <div><Label>Composio User ID</Label><Input value={connectForm.composio_user_id} onChange={(e) => setConnectForm({ ...connectForm, composio_user_id: e.target.value })} data-testid="yt-composio-user" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Cancelar</Button>
            <Button onClick={doConnect} data-testid="btn-do-connect-yt" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">Conectar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dlg-upload-yt">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Subir video a YouTube</DialogTitle>
            <DialogDescription>El video debe estar ya alojado en una URL pública (Tigris/S3, CDN). No se acepta base64.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} data-testid="yt-upload-title" /></div>
            <div><Label>Descripción</Label><Textarea rows={3} value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} data-testid="yt-upload-description" /></div>
            <div><Label>URL de video (mp4 público) *</Label><Input value={uploadForm.video_url} onChange={(e) => setUploadForm({ ...uploadForm, video_url: e.target.value })} placeholder="https://…mp4" data-testid="yt-upload-video-url" /></div>
            <div><Label>Miniatura</Label>
              <div className="flex flex-col sm:flex-row gap-2 items-start">
                <Input value={uploadForm.thumbnail_url} onChange={(e) => setUploadForm({ ...uploadForm, thumbnail_url: e.target.value })} placeholder="URL o subir" data-testid="yt-upload-thumb-url" />
                <label className="inline-flex items-center gap-1 rounded-md border border-input bg-white px-3 py-2 text-sm cursor-pointer hover:bg-stone-50">
                  {thumbUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadThumbnail(e.target.files?.[0])} data-testid="yt-upload-thumb-file" />
                  <span>Subir</span>
                </label>
              </div>
              {uploadForm.thumbnail_url && <img src={uploadForm.thumbnail_url} alt="thumb" className="mt-2 h-20 w-32 object-cover rounded border" />}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Categoría</Label>
                <Select value={uploadForm.category_id} onValueChange={(v) => setUploadForm({ ...uploadForm, category_id: v })}>
                  <SelectTrigger data-testid="yt-upload-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Privacidad</Label>
                <Select value={uploadForm.privacy} onValueChange={(v) => setUploadForm({ ...uploadForm, privacy: v })}>
                  <SelectTrigger data-testid="yt-upload-privacy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Público</SelectItem>
                    <SelectItem value="unlisted">No listado</SelectItem>
                    <SelectItem value="private">Privado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Tags (separados por coma)</Label><Input value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="marketing, tutorial, fstudy" data-testid="yt-upload-tags" /></div>
            <div><Label>Programar (opcional)</Label><Input type="datetime-local" value={uploadForm.scheduled_at} onChange={(e) => setUploadForm({ ...uploadForm, scheduled_at: e.target.value })} data-testid="yt-upload-scheduled-at" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancelar</Button>
            <Button onClick={doUpload} disabled={uploading} data-testid="btn-do-upload-yt" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" /> {uploadForm.scheduled_at ? "Programar" : "Subir ahora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={plOpen} onOpenChange={setPlOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Nueva playlist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={plForm.title} onChange={(e) => setPlForm({ ...plForm, title: e.target.value })} data-testid="pl-title" /></div>
            <div><Label>Descripción</Label><Textarea rows={2} value={plForm.description} onChange={(e) => setPlForm({ ...plForm, description: e.target.value })} /></div>
            <div><Label>Privacidad</Label>
              <Select value={plForm.privacy} onValueChange={(v) => setPlForm({ ...plForm, privacy: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Público</SelectItem>
                  <SelectItem value="unlisted">No listado</SelectItem>
                  <SelectItem value="private">Privado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlOpen(false)}>Cancelar</Button>
            <Button onClick={doCreatePlaylist} data-testid="btn-do-create-playlist" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Alerta por umbral</DialogTitle>
            <DialogDescription>Recibirás una notificación cuando la métrica supere el valor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Video ID</Label><Input value={alertForm.video_id} onChange={(e) => setAlertForm({ ...alertForm, video_id: e.target.value })} data-testid="alert-video-id" /></div>
            <div><Label>Métrica</Label>
              <Select value={alertForm.metric} onValueChange={(v) => setAlertForm({ ...alertForm, metric: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="views">Vistas</SelectItem>
                  <SelectItem value="likes">Likes</SelectItem>
                  <SelectItem value="comments">Comentarios</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Umbral</Label><Input type="number" value={alertForm.threshold} onChange={(e) => setAlertForm({ ...alertForm, threshold: e.target.value })} data-testid="alert-threshold" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertOpen(false)}>Cancelar</Button>
            <Button onClick={doCreateAlert} data-testid="btn-do-create-alert" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">Crear alerta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
